import { withAITracking } from "@microsoft/applicationinsights-react-js";
import React from "react";
import { withAuth0, WithAuth0Props } from "@auth0/auth0-react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { Card, Col, Container, Row } from "react-bootstrap";
import { isAfter, isBefore, subYears } from "date-fns";
import { isValidPhoneNumber } from "react-phone-number-input";
import CreateAccount from "models/account/create";
import CustomerProfileForm from "components/profile/CustomerProfileForm";
import PrimaryButton from "shared/components/PrimaryButton";
import LoadingSpinner from "shared/components/spinner/LoadingSpinner";
import accountService from "accessors/accountService";
import ToastUtility from "utilities/ToastUtility";
import InputValidators from "utilities/InputValidators";
import { reactPlugin } from "utilities/AppInsights";

interface CreateAccountState {
  form: CreateAccount;
  isLoading: boolean;
  validPhone: boolean;
  validEmail: boolean;
  termsOfUseAgree: boolean;
}

type Props = WithAuth0Props & RouteComponentProps;

class CreateAccountPage extends React.Component<Props, CreateAccountState> {
  currentYear: number = 0;

  constructor(props: Props) {
    super(props);

    this.currentYear = new Date().getFullYear();

    this.state = {
      form: {},
      isLoading: false,
      validPhone: false,
      validEmail: false,
      termsOfUseAgree: false,
    };
  }

  validateData(): boolean {
    if (!(this.state.termsOfUseAgree)) {
      return false;
    }

    const { form } = this.state;

    const validFirstName = form.firstName != undefined && form.firstName.length > 0;
    const validLastName = form.lastName != undefined && form.lastName.length > 0;

    const validDOB = form.dateOfBirth !== undefined
      && form.dateOfBirth.toString().length > 0
      && !isBefore(new Date(form.dateOfBirth), subYears(new Date(), 125))
      && !isAfter(new Date(form.dateOfBirth), new Date());

    let validBusinessName = true;
    if (form.businessName != undefined) validBusinessName = form.businessName.length > 0;

    const validAddress = form.streetAddress !== undefined && form.streetAddress.length > 0;
    const validCity = form.city !== undefined && form.city.length > 0;
    const validState = form.state !== undefined && form.state !== null && form.state.length > 0;
    const validZipCode = InputValidators.IsValidZipCode(form.zipCode ?? '');
    const validPhone = this.state.validPhone && form.phoneNumber != undefined && form.phoneNumber.length > 0;
    const validEmail = this.state.validEmail && form.emailAddress != undefined && form.emailAddress.length > 0;

    const validPassword = form.password != undefined && form.confirmPassword != undefined
      && InputValidators.IsValidPassword(form.password)
      && form.password.length > 0 && form.confirmPassword.length > 0
      && form.password === form.confirmPassword;

    const validFarmingStart = form.farmingStartYear !== undefined
      && form.farmingStartYear > 0
      && +form.farmingStartYear >= (this.currentYear - 100)
      && +form.farmingStartYear <= this.currentYear;

    return validFirstName && validLastName && validDOB && validBusinessName
      && validAddress && validCity && validState && validZipCode
      && validPhone && validEmail && validPassword && validFarmingStart;
  }

  async createAccountTapped() {
    if (!this.validateData()) return;

    this.setState({ isLoading: true });

    try {
      const accountExists = await accountService.CheckIfAccountExists(this.state.form.emailAddress ?? '');

      if (accountExists) {
        ToastUtility.showErrorToast('An account with that email already exists. Please log in or reset your password.');
        this.setState({ isLoading: false });
        return;
      }

      const success = await accountService.CreateAccount(this.state.form);

      if (success) {
        ToastUtility.showSuccessToast('Account created successfully! You can now sign in.');
        this.props.history.push('/');
      } else {
        ToastUtility.showErrorToast('Error creating account. Please try again.');
        this.setState({ isLoading: false });
      }
    } catch (error) {
      console.log(error);
      ToastUtility.showErrorToast('Error creating account. Please try again.');
      this.setState({ isLoading: false });
    }
  }

  render() {
    const { form, isLoading, termsOfUseAgree } = this.state;

    return (
      <Container className="py-4">
        {isLoading && (
          <LoadingSpinner variant={'dark'} animation={'border'} />
        )}
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8}>
            <Card className="p-4">
              <h2 className="mb-4 text-center">Create Account</h2>
              <CustomerProfileForm
                mode="create"
                email={form.emailAddress}
                firstName={form.firstName}
                lastName={form.lastName}
                suffix={form.suffix}
                dateOfBirth={form.dateOfBirth}
                businessName={form.businessName}
                streetAddress={form.streetAddress}
                streetAddress2={form.streetAddress2}
                city={form.city}
                state={form.state}
                zipCode={form.zipCode}
                phoneNumber={form.phoneNumber}
                farmingStartYear={form.farmingStartYear}
                password={form.password}
                confirmPassword={form.confirmPassword}
                termsOfUseAgree={termsOfUseAgree}
                onEmailChange={(e) => {
                  const emailAddress = e.target.value;
                  const validEmail = InputValidators.IsValidEmailAddress(emailAddress);
                  this.setState(prev => ({ form: { ...prev.form, emailAddress }, validEmail }));
                }}
                onFirstNameChange={(e) => this.setState(prev => ({ form: { ...prev.form, firstName: e.target.value } }))}
                onLastNameChange={(e) => this.setState(prev => ({ form: { ...prev.form, lastName: e.target.value } }))}
                onSuffixChange={(e) => this.setState(prev => ({ form: { ...prev.form, suffix: e.target.value } }))}
                onDOBChange={(date) => this.setState(prev => ({ form: { ...prev.form, dateOfBirth: date } }))}
                onBusinessNameChange={(e) => this.setState(prev => ({ form: { ...prev.form, businessName: e.target.value } }))}
                onStreetAddressChange={(e) => this.setState(prev => ({ form: { ...prev.form, streetAddress: e.target.value } }))}
                onStreetAddress2Change={(e) => this.setState(prev => ({ form: { ...prev.form, streetAddress2: e.target.value } }))}
                onCityChange={(e) => this.setState(prev => ({ form: { ...prev.form, city: e.target.value } }))}
                onStateChange={(e) => this.setState(prev => ({ form: { ...prev.form, state: e.target.value } }))}
                onZipCodeChange={(e) => this.setState(prev => ({ form: { ...prev.form, zipCode: e.target.value } }))}
                onPhoneChange={(value, data, event, formattedValue) => {
                  const validPhone = isValidPhoneNumber(`+1 ${formattedValue}`);
                  this.setState(prev => ({ form: { ...prev.form, phoneNumber: value }, validPhone }));
                }}
                onFarmingStartYearChange={(e) => this.setState(prev => ({ form: { ...prev.form, farmingStartYear: +e.target.value } }))}
                onPasswordChange={(e) => this.setState(prev => ({ form: { ...prev.form, password: e.target.value } }))}
                onConfirmPasswordChange={(e) => this.setState(prev => ({ form: { ...prev.form, confirmPassword: e.target.value } }))}
                onTermsAgreeChange={(checked) => this.setState({ termsOfUseAgree: checked })}
              />
              <div className="mt-3 d-flex justify-content-center">
                <PrimaryButton
                  buttonText="Create Account"
                  onClick={this.createAccountTapped.bind(this)}
                  disabled={!this.validateData()}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default withRouter(withAuth0(withAITracking(reactPlugin, CreateAccountPage)));
